import { PdfMediaFile } from "@/features/media/types/media.types";
import type { Survey } from "@/features/survey/types";
import { parseHyperlinkField } from "@/shared/utils/hyperlinkUtils";
import dayjs from "dayjs";
import type { ReactNode } from "react";
import { createElement, Fragment } from "react";

export const formatDate = (date: Date | string | number): string => {
  if (!date) return "";
  return dayjs(date).format("MMM. D, YYYY");
};

/** Checks if uploaded media is a PDF file for direct embedding */
export const isPdfFile = (file: PdfMediaFile): boolean =>
  file.mediaType === "application/pdf" ||
  file.displayName?.toLowerCase().endsWith(".pdf") ||
  file.keyName?.toLowerCase().endsWith(".pdf") ||
  false;

/**
 * Flattens media files by replacing original files with their modified versions.
 * - Finds files where item.mediaId === res.modifiedFromMediaId
 * - Replaces the original with the modified version
 * - Removes the duplicate modified entry
 */
export const flattenMediaFilesWithModifiedFiles = <
  T extends { mediaId: string; modifiedFromMediaId?: string },
>(
  mediaFiles: T[],
): T[] => {
  if (!mediaFiles || mediaFiles.length === 0) return [];

  const modifiedFilesMap = new Map<string, T>();
  const regularFiles: T[] = [];

  mediaFiles.forEach((file) => {
    if (file.modifiedFromMediaId) {
      modifiedFilesMap.set(file.modifiedFromMediaId, file);
    } else {
      regularFiles.push(file);
    }
  });

  return regularFiles.map((file) => modifiedFilesMap.get(file.mediaId) ?? file);
};

export const hasAddressLine2AsCustom = (currentSurvey: Survey) => {
  return currentSurvey?.surveyPropertyFieldRule?.propertyFieldRuleList?.some(
    (rule) => rule.fieldLabel === "Address Line 2" && !rule.fieldKey,
  );
};

export const isUrl = (value: unknown): boolean => {
  if (typeof value !== "string") return false;

  const trimmed = value.trim();
  const urlWithProtocol = /^https?:\/\/[^\s<>"']+$/i;
  if (urlWithProtocol.test(trimmed)) return true;

  const urlWithoutProtocol =
    /^(www\.[^\s<>"']+|[a-zA-Z0-9-]+\.[a-zA-Z]{2,}[^\s<>"']*)$/i;
  return urlWithoutProtocol.test(trimmed);
};

const containsHtmlTags = (str: string): boolean => {
  return /<[^>]+>/i.test(str);
};

const processTextWithUrls = (text: string): ReactNode[] => {
  const urlRegex =
    /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+|(?:^|\s)([a-zA-Z0-9-]+\.[a-zA-Z]{2,}[^\s<>"']*))/g;
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = urlRegex.exec(text)) !== null) {
    const rawMatch = match[0];
    const url = rawMatch.trim();
    const leadingWhitespace = rawMatch.length - rawMatch.trimStart().length;
    const matchIndex = match.index + leadingWhitespace;

    if (matchIndex > lastIndex) {
      parts.push(text.slice(lastIndex, matchIndex));
    }

    const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    const linkElement = createElement(
      "a",
      {
        href,
        target: "_blank",
        rel: "noopener noreferrer",
        className: "text-primary underline",
      },
      url,
    );

    parts.push(linkElement);
    lastIndex = matchIndex + url.length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
};

export const renderUrlOrValue = (
  value: unknown,
  fieldKey?: string | null,
): ReactNode | string => {
  if (typeof value !== "string") {
    return String(value);
  }

  let stringValue = value;
  const trimmedValue = value.trim();
  const looksLikeHyperlinkJsonArray =
    trimmedValue.startsWith("[") && trimmedValue.endsWith("]");

  if (looksLikeHyperlinkJsonArray) {
    const entries = parseHyperlinkField(value);
    const isStructured =
      entries.length > 1 ||
      (entries.length === 1 &&
        (entries[0].text?.trim() || "") !== "" &&
        entries[0].text !== entries[0].url);

    if (isStructured) {
      return createElement(
        Fragment,
        {},
        ...entries.map((entry, index) => {
          const href = /^https?:\/\//i.test(entry.url)
            ? entry.url
            : `https://${entry.url}`;
          const display = entry.text?.trim() || entry.url;

          return createElement(
            "div",
            { key: index },
            createElement(
              "a",
              {
                href,
                target: "_blank",
                rel: "noopener noreferrer",
                className: "text-primary underline",
              },
              display,
            ),
          );
        }),
      );
    }

    if (entries.length === 1 && entries[0].url) {
      stringValue = entries[0].url;
    }
  }

  if (isUrl(stringValue)) {
    const href = /^https?:\/\//i.test(stringValue)
      ? stringValue
      : `https://${stringValue}`;

    return createElement(
      "a",
      {
        href,
        target: "_blank",
        rel: "noopener noreferrer",
        className: "text-primary underline",
      },
      stringValue,
    );
  }

  if (containsHtmlTags(stringValue)) {
    const processedContent = processTextWithUrls(stringValue);
    return createElement(Fragment, {}, ...processedContent);
  }

  const hasLineBreaks = stringValue.includes("\n");
  const shouldWrapInDiv = hasLineBreaks || fieldKey === "address1";

  return stringValue.split("\n").map((line, index) => {
    const isEmptyLine = line.trim() === "";
    const contentHash =
      line.length > 0 ? line.slice(0, 5).replace(/\s/g, "") : "empty";
    const uniqueKey = `line-${index}-${contentHash}`;
    const processedContent = processTextWithUrls(line);

    if (shouldWrapInDiv) {
      return createElement(
        "div",
        { key: uniqueKey, className: isEmptyLine ? "" : undefined },
        ...processedContent,
      );
    }

    const hasReactElements = processedContent.some(
      (part) => typeof part !== "string",
    );

    if (hasReactElements || processedContent.length > 1) {
      return createElement(Fragment, { key: uniqueKey }, ...processedContent);
    }

    return processedContent.join("");
  });
};

export {
  getPropertyDisplayNumber,
  getPropertyMarkerContent,
} from "./propertyUtils";
